/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import { Injectable } from '@angular/core';
import * as _ from 'underscore';

@Injectable({
  providedIn: 'root'
})
export class ExamCacheService {
  private associateExamCache = new Map<any, any>();
  private skipExamCache = new Map<any, any>();
  private assignedExamCache = new Map<any, any>();
  private workItemInfoCache = new Map<any, any>();
  private assignedWorklistIdCache = new Map<any, any>();
  private sendStudyInDicomCache = new Map<any, any>();
  private sendSeriesInDicomCache = new Map<any, any>();
  private tabExamCache = new Map<any, any>();
  private tabWorklistIdCache = new Map<any, any>();
  private exceptionWLIdCache = new Map<any, any>();

  clearExamCache() {
    this.associateExamCache.clear();
    this.skipExamCache.clear();
    this.assignedExamCache.clear();
    this.workItemInfoCache.clear();
    this.assignedWorklistIdCache.clear();
    this.sendStudyInDicomCache.clear();
    this.sendSeriesInDicomCache.clear();
    this.tabExamCache.clear();
    this.tabWorklistIdCache.clear();
    this.exceptionWLIdCache.clear();
  }

  getAssociateExamCache(workItemId) {
    return this.associateExamCache.get(workItemId);
  }

  setAssociateExamCache(workItemId) {
    return this.associateExamCache.set(workItemId, true);
  }

  clearAssociateExamsCache() {
    this.associateExamCache.clear();
  }

  removeAssociateExamsCache(workItemId): boolean {
    return this.associateExamCache.delete(workItemId);
  }

  getSkipAutoAdvanceExamCache(workItemId) {
    return this.skipExamCache.get(workItemId);
  }

  setSkipAutoAdvanceExamCache(workItemId) {
    return this.skipExamCache.set(workItemId, true);
  }

  clearSkipAutoAdvanceExamCache() {
    this.skipExamCache.clear();
  }

  getAssignedExamCache(workItemId) {
    return this.assignedExamCache.get(workItemId);
  }

  setAssignedExamCache(workItemId) {
    return this.assignedExamCache.set(workItemId, true);
  }

  removeAssignedExamCache(workItemId) {
    return this.assignedExamCache.delete(workItemId);
  }

  setWorkItemInfo(workItemId, workListName) {
    return this.workItemInfoCache.set(workItemId, workListName);
  }

  getWorkItemInfo(workItemId) {
    return this.workItemInfoCache.get(workItemId);
  }

  removeWorkItemInfo(workItemId) {
    return this.workItemInfoCache.delete(workItemId);
  }

  setAssignedWorklistId(workItemId) {
    return this.assignedWorklistIdCache.set('id', workItemId);
  }

  getAssignedWorklistId() {
    return this.assignedWorklistIdCache.get('id');
  }

  getSendStudyInDicom(key) {
    return this.sendStudyInDicomCache.get(key);
  }

  updateSendStudyInDicom(serverId, studyList) {
    return this.sendStudyInDicomCache.set(serverId, JSON.stringify(studyList));
  }

  removeAllSendStudyInDicom() {
    this.sendStudyInDicomCache.clear();
    return this.sendStudyInDicomCache.clear();
  }

  removeSendStudyInDicom(key) {
    return this.sendStudyInDicomCache.delete(key);
  }

  getSeriesInDicomInfo(key) {
    return this.sendSeriesInDicomCache.get(key);
  }

  updateSeriesInDicomInfo(serverId, studyList) {
    return this.sendSeriesInDicomCache.set(serverId, studyList);
  }

  removeAllSeriesInDicomInfo() {
    return this.sendSeriesInDicomCache.clear();
  }

  removeSeriesInDicomInfo(key) {
    return this.sendSeriesInDicomCache.delete(key);
  }

  getTabExamCache(workItemId) {
    return this.tabExamCache.get(workItemId);
  }

  setTabExamCache(workItemId) {
    return this.tabExamCache.set(workItemId, true);
  }

  removeTabExamCache(workItemId) {
    return this.tabExamCache.delete(workItemId);
  }

  removeAllTabExamCache() {
    return this.tabExamCache.clear();
  }

  removeSelectedTabExamCache(ids) {
    _.each(ids, id => {
      this.tabExamCache.delete(id);
    });
  }

  setTabWorklistId(workItemId) {
    return this.tabWorklistIdCache.set('id', workItemId);
  }

  getTabWorklistId() {
    return this.tabWorklistIdCache.get('id');
  }

  setExceptionWorklistId(workItemId) {
    return this.exceptionWLIdCache.set('id', workItemId);
  }

  getExceptionWorklistId() {
    return this.exceptionWLIdCache.get('id');
  }
}
